<template lang="pug">
    div.post-view(:class="theClass")
        div.post-view__top
            creator-control.post-view__creator(:creator="creator")
            div.post-view__date(@click="goToPost") {{ createdAt }}
            div.post-view__menu(v-if="isPostOwner" @click="openMenu" ref="menuButton")
                svg-icon(iconId="menu-dots-2")

        div.post-view__content(v-if="showLocker || showSlider")
            div.post-view__locker(v-if="showLocker")
                img(:src="lockerThumbnailImageSrc" :style="lockerThumbnailStyle")
                div.post-view__locker-actions
                    button-control(v-for="lockerType in lockerTypes" :key="lockerType" @click="clickUnlock(lockerType)") {{ getUnlockButtonText(lockerType) }}

            div.post-view__slides-container(v-if="showSlider" :style="slidesContainerStyle")
                div.post-view__slides.splide(ref="splide")
                    div.splide__track
                        div.splide__list
                            div.splide__slide(v-for="(mediaFile, i) in mediaFiles" :key="mediaFile.url")
                                div.post-view__slide
                                    img.post-view__slide-image(v-if="mediaFile.type == 'image'" :src="mediaFile.url")
                                    video-player-control.post-view__slide-video(v-else :asset="mediaFile" :fillMode="true" :clickToPlay="false")
                                    div.post-view__slide-info(v-if="!singleMedia")
                                        svg-icon(iconId='picture')
                                        span {{ getMediaInfoText(i) }}

            resize-sensor(@resize="contentResize")

        div.post-view__stats
            div.post-view__stat(:class="{ 'post-view__stat-like--liked': post.is_liked }")
                div(@click="toggleLike")
                    svg-icon(iconId="heart")
                span(@click="openLikes") {{ likesCount }}
            div.post-view__stat(@click="toggleShowComments")
                svg-icon(iconId="comment")
                span {{ commentsCount }}
            div.post-view__stat(v-if="!isPostOwner && creator.creator" @click="showSendTipModal = true")
                svg-icon(iconId="dollar")
                span.send-tip send tip
            div.post-view__stat-grow
            div.post-view__stat(@click="openFlagContentModal")
                svg-icon(iconId="flag")

        send-tip-modal(v-if="showSendTipModal" :user="post.user.data" @close="showSendTipModal = false")

        div.post-view__description(v-show="description" v-html="description")

        post-comments(v-show="showComments" ref="postComments" :post-id="post.id" :comments="post.comments.data" :user="post.user.data" @sendComment="pushComment")
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import Splide from '@splidejs/splide'
import '@splidejs/splide/dist/css/themes/splide-default.min.css'
import EventBus from '../../lib/event-bus'
import Events from '../../lib/events'
import PostLikeService from '../../services/post-like-service'
import UserProductsService from '../../services/user-products-service'
import PostService from '../../services/post-service'
import ModalsList from '../modals/modals-list'

export default {
    name: 'post-view',

    props: {
        post: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loaded: false,
            contentWidth: null,
            middleScale: null,
            splide: null,
            showComments: false,
            showSendTipModal: false,
            showFlagContentModal: false,
            maxHeight: null,
        }
    },

    computed: {
        ...mapGetters(['authUser']),

        isPostOwner() {
            return this.creator.id === this.authUser.id
        },

        creator() {
            return this.post.user.data
        },
        createdAt() {
            const m = moment(this.post.created_at)

            if (m.year() == moment().year()) {
                return m.format('MMM D, h:mm A')
            }

            return m.format('MMM D YYYY, h:mm A')
        },
        mediaFiles() {
            return this.post.files.data.filter(f => !f.group)
        },
        hasMedia() {
            return this.mediaFiles.length > 0
        },
        singleMedia() {
            return this.mediaFiles.length == 1
        },

        showLocker() {
            return this.locker && this.lockerThumbnailImageSrc
        },
        showSlider() {
            return !this.locker && this.hasMedia
        },

        likesCount() {
            return this.post.likes_count
        },
        commentsCount() {
            return this.post.comments_count
        },
        description() {
            return this.post.content.descriptionShow
        },
        locker() {
            return this.post.content.locker
        },

        lockerThumbnailImageSrc() {
            return this.post.files.data.find(f => f.group == 'locker-thumbnail')?.url
        },
        lockerTypes() {
            return Array.isArray(this.locker.lockerType) ? this.locker.lockerType : [this.locker.lockerType]
        },

        sliderWidth() {
            return `${this.contentWidth}px`
        },
        sliderHeight() {
            let height = this.contentWidth / this.middleScale
            if (height > this.maxHeight) {
                height = this.maxHeight
            }
            return `${height}px`
        },

        slidesContainerStyle() {
            return {
                height: this.sliderHeight,
            }
        },

        menuOptions() {
            return [
                { value: 'edit', name: 'Edit' },
                { value: 'delete', name: 'Delete' },
            ]
        },

        theClass() {
            return {
                'post-view--loaded': this.loaded,
                'post-view--owner': this.isPostOwner,
            }
        },

        lockerThumbnailStyle() {
            return {
                'max-height': `${this.maxHeight}px`,
            }
        },
    },

    watch: {
        post() {
            this.initialize()
        },
    },

    mounted() {
        this.initialize()
    },

    methods: {
        initialize() {
            this.loaded = false
            this.preloadImages(() => {
                if (this.$refs.splide) {
                    this.splide = new Splide(this.$refs.splide, {
                        rewind: true,
                        gap: 20,
                        pagination: false,
                        perPage: 1,
                        arrows: !this.singleMedia,
                        width: this.sliderWidth,
                        height: this.sliderHeight,
                    }).mount()
                }
            })
        },

        goToPost() {
            this.$router.push(`/post/${this.post.id}`)
        },
        getUnlockButtonText(lockerType) {
            switch (lockerType) {
                case 'one-time-payment':
                    return `Pay $${this.locker.price}`
                case 'subscription':
                    return 'Subscribe'
            }
        },
        async clickUnlock(lockerType) {
            if (this.isPostOwner) {
                return
            }

            switch (lockerType) {
                case 'one-time-payment':
                    const product = await UserProductsService.firstOfType(this.post.user.data.id, 'single_post')

                    this.$store.dispatch('Shop/Orders/Cart/addCartItem', {
                        id: `post_${this.post.id}_content_via_${product.id}`,
                        type: 'content',
                        payload: {
                            product_id: product.id,
                            options: {
                                post_id: this.post.id,
                            },
                        },
                        attributes: {
                            product: product,
                        },
                        total: this.locker.price,
                    })

                    this.$router.push({ name: 'shopping-cart-page' })

                    break
                case 'subscription':
                    EventBus.$emit(Events.OPEN_MODAL, ModalsList.SUBSCRIBE_MODAL, {
                        user: this.creator,
                        subscriptionIds: this.locker.subscriptions,
                    })
                    break
            }
        },

        toggleLike() {
            if (this.post.is_liked) {
                PostLikeService.destroy(this.post.id).then(() => {
                    this.post.is_liked = false
                    this.post.likes_count--
                })
            } else {
                PostLikeService.store(this.post.id).then(() => {
                    this.post.is_liked = true
                    this.post.likes_count++
                })
            }
        },
        openLikes() {
            EventBus.$emit(Events.OPEN_MODAL, ModalsList.LIKES_MODAL, {
                post: this.post,
            })
        },
        toggleShowComments() {
            this.showComments = !this.showComments
        },
        pushComment(comment) {
            this.post.comments.data.push(comment)
            this.post.comments_count++

            setTimeout(() => {
                this.$refs.postComments.scrollToLastComment()
            }, 100)
        },

        getMediaInfoText(idx) {
            return `${idx + 1}/${this.mediaFiles.length}`
        },

        preloadImages(onload) {
            const promises = []
            const scales = []
            const heights = []

            this.mediaFiles.filter(m => m.type == 'image').forEach(m => {
                promises.push(new Promise((resolve) => {
                    const img = new Image()
                    img.onload = () => {
                        scales.push(img.width / img.height)
                        heights.push(img.height)
                        resolve()
                    }
                    img.src = m.url
                }))
            })

            if (scales.length == 0) {
                scales.push(16 / 9)
            }

            Promise.all(promises).then(() => {
                this.middleScale = (Math.min(...scales) + Math.max(...scales)) / 2
                const maxHeight = Math.max(...heights)
                this.maxHeight = maxHeight > 0 && maxHeight < 400 ? maxHeight : 400
                this.loaded = true
                onload()
            })
        },

        contentResize(e) {
            this.contentWidth = e.width
            if (this.splide) {
                this.splide.options = {
                    width: this.sliderWidth,
                    height: this.sliderHeight,
                }
            }
        },

        openMenu() {
            EventBus.$emit(Events.OPEN_MODAL, ModalsList.SELECT_OPTION, {
                element: this.$refs.menuButton,
                options: this.menuOptions,
                handler: this.selectMenu,
                offsetX: 1,
                placement: 'bottom-end',
            })
        },
        selectMenu(option) {
            switch (option.value) {
                case 'edit':
                    this.$router.push(`/post/edit/${this.post.id}`)
                    break
                case 'delete':
                    EventBus.$emit(Events.OPEN_MODAL, ModalsList.GENERIC_CONFIRMATION, {
                        text: 'Are you sure you want to delete post?',
                        approve: () => {
                            PostService.delete(this.post.id).then(() => {
                                this.$emit('removed')
                            })
                        },
                        confirmButtonText: 'Delete',
                    })
                    break
            }
        },

        openFlagContentModal() {
            EventBus.$emit(Events.OPEN_MODAL, ModalsList.FLAG_CONTENT, {
                post: this.post,
            })
        }
    },
}
</script>
